<template>
  <div class="whatsapp-integration-card">
    <!-- Header do Card -->
    <div class="integration-header">
      <div class="integration-icon whatsapp-icon">
        <svg class="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </div>
      <div class="integration-info">
        <h3 class="integration-title">{{ t('integracao.whatsappTitle') }}</h3>
        <div v-if="connection && connection.status === 'CONNECTED'" class="status-badge connected">
          <span class="status-dot"></span>
          {{ t('integracao.connected') }}
        </div>
        <div v-else class="status-badge disconnected">
          <span class="status-dot"></span>
          {{ t('integracao.disconnected') }}
        </div>
      </div>
    </div>

    <!-- Descrição -->
    <p class="integration-description">
      {{ t('integracao.whatsappDescription') }}
    </p>

    <div v-if="!planAllowsWhatsapp" class="whatsapp-plan-upgrade">
      <p>{{ t('integracao.whatsappUpgrade') }} <strong>{{ t('integracao.whatsappUpgradePlan') }}</strong> {{ t('integracao.whatsappUpgradeSuffix') }}</p>
      <button type="button" class="btn-connect" @click="goUpgrade">{{ t('integracao.viewPlans') }}</button>
    </div>

    <template v-else>
    <!-- Estado: Desconectado -->
    <div v-if="!connection || connection.status === 'DISCONNECTED'" class="integration-content">
      <button @click="iniciarConexao" class="btn-connect" :disabled="loading">
        <svg v-if="loading" class="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {{ loading ? t('integracao.connecting') : t('integracao.connectWhatsapp') }}
      </button>
    </div>

    <!-- Estado: Conectando (mostra QR Code) -->
    <div v-else-if="connection.status === 'CONNECTING'" class="integration-content">
      <div class="qr-code-container">
        <h4 class="qr-title">{{ t('integracao.scanQrTitle') }}</h4>
        <p class="qr-instructions">
          {{ t('integracao.qrStep1') }}<br>
          {{ t('integracao.qrStep2') }}<br>
          {{ t('integracao.qrStep3') }}
        </p>
        <p v-if="gatewayUnreachable" class="gateway-error">
          {{ t('integracao.gatewayError404') }}
        </p>
        <p v-else-if="gatewayStartFailed" class="gateway-error">
          {{ t('integracao.gatewayStartFailed') }}
        </p>
        <div v-else class="qr-code-box">
          <img v-if="connection.qrCode" :src="connection.qrCode" :alt="t('integracao.qrAlt')" class="qr-code-image">
          <div v-else class="qr-loading">
            <svg class="animate-spin w-8 h-8" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>
        <button @click="cancelarConexao" class="btn-secondary">
          {{ t('common.cancel') }}
        </button>
        
        <!-- Botão para simular conexão (desenvolvimento) -->
        <button @click="simularConexao" class="btn-simulate">
          {{ t('integracao.simulateConnection') }}
        </button>
      </div>
    </div>

    <!-- Estado: Conectado -->
    <div v-else-if="connection.status === 'CONNECTED'" class="integration-content">
      <div class="connected-info">
        <div class="info-item">
          <span class="info-label">{{ t('integracao.phoneConnected') }}</span>
          <span class="info-value">{{ connection.phoneNumber || 'N/A' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">{{ t('integracao.statusLabel') }}</span>
          <span class="info-value status-active">{{ t('integracao.statusActive') }}</span>
        </div>
      </div>
      <div class="connected-actions">
        <button v-if="showChatbot" type="button" class="btn-chatbot" @click="goChatbot">
          {{ t('integracao.chatbot.configure') }}
          <span v-if="chatbotAtivo" class="chatbot-badge active">{{ t('integracao.chatbot.badgeActive') }}</span>
          <span v-else class="chatbot-badge">{{ t('integracao.chatbot.badgeInactive') }}</span>
        </button>
        <button @click="desconectar" class="btn-disconnect" :disabled="loading">
          {{ loading ? t('integracao.disconnecting') : t('integracao.disconnect') }}
        </button>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import { config } from '@/config/app'
import { usePlanoFeatures } from '@/composables/usePlanoFeatures'
import { useWorkspaceStore } from '@/stores/workspace'
import { useGlobalWorkspaces } from '@/composables/useGlobalWorkspaces'
import { isServicosWorkspace } from '@/utils/workspaceTipo'

export default {
  name: 'WhatsAppIntegrationCard',
  props: {
    workspaceId: {
      type: Number,
      required: true
    }
  },
  emits: ['connection-updated'],
  setup(props, { emit }) {
    const router = useRouter()
    const { t } = useI18n()
    const workspaceStore = useWorkspaceStore()
    const { currentWorkspace } = useGlobalWorkspaces()
    const workspaceForTipo = computed(
      () => currentWorkspace.value ?? workspaceStore.currentWorkspace
    )
    const showChatbot = computed(() => !isServicosWorkspace(workspaceForTipo.value))
    const planoFeatures = usePlanoFeatures()
    const planAllowsWhatsapp = ref(true)
    const connection = ref(null)
    const loading = ref(false)
    const qrCodePolling = ref(null)
    const gatewayUnreachable = ref(false)
    const gatewayStartFailed = ref(false)
    const chatbotAtivo = ref(false)

    const goUpgrade = () => router.push('/upgrade')

    const goChatbot = () => {
      router.push({ name: 'WorkspaceWhatsAppChatbot', params: { id: props.workspaceId } })
    }

    const loadChatbotStatus = async () => {
      try {
        const { data } = await api.get(`/whatsapp/workspace/${props.workspaceId}/chatbot`)
        chatbotAtivo.value = !!data.ativo
      } catch {
        chatbotAtivo.value = false
      }
    }

    const pararPolling = () => {
      if (qrCodePolling.value) {
        clearInterval(qrCodePolling.value)
        qrCodePolling.value = null
      }
    }

    const buscarConexao = async () => {
      try {
        const response = await api.get(`/whatsapp/workspace/${props.workspaceId}`)
        connection.value = response.data
        console.log('📡 Conexão WhatsApp carregada:', connection.value)
        if (connection.value?.status === 'CONNECTED' || connection.value?.status === 'DISCONNECTED') {
          pararPolling()
        }
        return connection.value
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('❌ Erro ao buscar conexão WhatsApp:', error)
        }
        connection.value = null
        return null
      }
    }

    const iniciarPollingConexao = () => {
      pararPolling()
      gatewayUnreachable.value = false
      qrCodePolling.value = setInterval(async () => {
        if (connection.value?.status !== 'CONNECTING') {
          pararPolling()
          return
        }
        try {
          const resp = await fetch(`${config.whatsGatewayUrl}/gateway/qr/${props.workspaceId}`)
          if (resp.status === 404) {
            gatewayUnreachable.value = true
            return
          }
          if (!resp.ok) {
            return
          }
          gatewayUnreachable.value = false
          const data = await resp.json()
          if (data?.qrCode && connection.value?.status === 'CONNECTING') {
            connection.value = { ...connection.value, qrCode: data.qrCode }
          }
        } catch (_) {
          /* gateway pode estar offline */
        }
        const atual = await buscarConexao()
        if (atual?.status === 'CONNECTED') {
          emit('connection-updated', atual)
        }
      }, 2000)
    }

    const onWhatsAppConnected = async () => {
      const atual = await buscarConexao()
      if (atual) {
        await loadChatbotStatus()
        emit('connection-updated', atual)
      }
    }

    const onWhatsAppDisconnected = async () => {
      connection.value = null
      pararPolling()
      emit('connection-updated', null)
    }

    const iniciarConexao = async () => {
      if (!planAllowsWhatsapp.value) {
        goUpgrade()
        return
      }
      try {
        loading.value = true
        gatewayUnreachable.value = false
        gatewayStartFailed.value = false
        console.log('🔌 Iniciando conexão WhatsApp...')
        const response = await api.post(`/whatsapp/workspace/${props.workspaceId}/connect`)
        connection.value = response.data
        console.log('✅ Conexão iniciada, QR Code gerado')
        emit('connection-updated', connection.value)

        try {
          const startResp = await fetch(`${config.whatsGatewayUrl}/gateway/start/${props.workspaceId}`, { method: 'POST' })
          if (startResp.status === 404) {
            gatewayUnreachable.value = true
          } else if (!startResp.ok) {
            gatewayStartFailed.value = true
          }
        } catch (e) {
          gatewayStartFailed.value = true
          console.warn('Gateway WhatsApp indisponível; use Simular Conexão (Dev) ou inicie W_start_whatsapp_gateway.bat')
        }
        iniciarPollingConexao()
      } catch (error) {
        console.error('❌ Erro ao iniciar conexão:', error)
        alert('Erro ao iniciar conexão com WhatsApp. Tente novamente.')
      } finally {
        loading.value = false
      }
    }

    const simularConexao = async () => {
      try {
        loading.value = true
        const phoneNumber = prompt('Digite o número do WhatsApp (formato: 5511999999999):')
        if (!phoneNumber) return
        console.log('✅ Simulando conexão com número:', phoneNumber)
        const response = await api.post(`/whatsapp/workspace/${props.workspaceId}/confirm`, {
          phoneNumber: phoneNumber
        })
        connection.value = response.data
        pararPolling()
        console.log('✅ Conexão confirmada com sucesso')
        emit('connection-updated', connection.value)
      } catch (error) {
        console.error('❌ Erro ao confirmar conexão:', error)
        alert('Erro ao confirmar conexão. Tente novamente.')
      } finally {
        loading.value = false
      }
    }

    const desconectar = async () => {
      if (!confirm('Tem certeza que deseja desconectar o WhatsApp?')) {
        return
      }
      try {
        loading.value = true
        console.log('🔌 Desconectando WhatsApp...')
        await api.post(`/whatsapp/workspace/${props.workspaceId}/disconnect`)
        connection.value = { status: 'DISCONNECTED' }
        pararPolling()
        console.log('✅ WhatsApp desconectado')
        emit('connection-updated', null)
      } catch (error) {
        console.error('❌ Erro ao desconectar:', error)
        alert('Erro ao desconectar WhatsApp. Tente novamente.')
      } finally {
        loading.value = false
      }
    }

    const cancelarConexao = async () => {
      await desconectar()
    }

    onMounted(async () => {
      await planoFeatures.loadForWorkspace(props.workspaceId)
      planAllowsWhatsapp.value = planoFeatures.can('crmWhatsapp')
      await buscarConexao()
      if (connection.value?.status === 'CONNECTED' && showChatbot.value) {
        await loadChatbotStatus()
      }
      if (connection.value?.status === 'CONNECTING') {
        iniciarPollingConexao()
      }
      window.addEventListener('whatsapp.connected', onWhatsAppConnected)
      window.addEventListener('whatsapp.disconnected', onWhatsAppDisconnected)
    })

    onUnmounted(() => {
      pararPolling()
      window.removeEventListener('whatsapp.connected', onWhatsAppConnected)
      window.removeEventListener('whatsapp.disconnected', onWhatsAppDisconnected)
    })

    return {
      t,
      connection,
      loading,
      gatewayUnreachable,
      gatewayStartFailed,
      planAllowsWhatsapp,
      showChatbot,
      chatbotAtivo,
      goChatbot,
      goUpgrade,
      iniciarConexao,
      simularConexao,
      desconectar,
      cancelarConexao
    }
  }
}
</script>

<style scoped>
.whatsapp-plan-upgrade {
  margin: 16px 0;
  padding: 20px;
  border-radius: 4px;
  border: 1px dashed color-mix(in srgb, var(--accent-primary) 45%, var(--border-primary));
  background: color-mix(in srgb, var(--accent-primary) 8%, var(--bg-secondary));
  text-align: center;
}
.whatsapp-plan-upgrade p {
  margin: 0 0 14px;
  color: var(--text-secondary);
  font-size: 14px;
}
.whatsapp-integration-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 24px;
  transition: all 0.3s ease;
}

.whatsapp-integration-card:hover {
  border-color: var(--border-hover);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.integration-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.integration-icon {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.whatsapp-icon {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  color: white;
}

.integration-info {
  flex: 1;
}

.integration-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
}

.status-badge.connected {
  background: #dcfce7;
  color: #166534;
}

.status-badge.disconnected {
  background: #fee2e2;
  color: #991b1b;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.integration-description {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 20px;
}

.integration-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn-connect {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-connect:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
}

.btn-connect:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.connected-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.btn-chatbot {
  width: 100%;
  padding: 10px 16px;
  background: color-mix(in srgb, #25d366 18%, var(--bg-primary));
  color: var(--text-primary);
  border: 1px solid #25d366;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.chatbot-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-weight: 500;
}

.chatbot-badge.active {
  background: #25d366;
  color: #fff;
}

.btn-disconnect {
  width: 100%;
  padding: 10px 24px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-disconnect:hover:not(:disabled) {
  background: #dc2626;
}

.btn-disconnect:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  width: 100%;
  padding: 10px 24px;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-secondary:hover {
  background: var(--bg-hover);
  border-color: var(--border-hover);
}

.btn-simulate {
  width: 100%;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-simulate:hover {
  background: #2563eb;
}

.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px 0;
}

.qr-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: center;
}

.qr-instructions {
  font-size: 13px;
  color: var(--text-secondary);
  text-align: center;
  line-height: 1.6;
}

.qr-code-box {
  width: 240px;
  height: 240px;
  background: white;
  border: 2px solid var(--border-primary);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.qr-code-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-loading {
  color: #9ca3af;
}

.gateway-error {
  margin: 12px 0;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #f59e0b;
  background: rgba(245, 158, 11, 0.12);
  color: var(--text-primary, #1f2937);
  font-size: 13px;
  line-height: 1.5;
}

.gateway-error code {
  font-size: 12px;
}

.connected-info {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 12px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.info-item:not(:last-child) {
  border-bottom: 1px solid var(--border-primary);
}

.info-label {
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 500;
}

.info-value {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 600;
}

.status-active {
  color: #16a34a;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

